import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Section, Kicker, SerifTitle } from "@/components/Section";
import { EditorialImage } from "@/components/EditorialImage";
import { HeroVideo, heroTextShadow } from "@/components/HeroVideo";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAboutImage, usePortfolio } from "@/lib/content";

export default function About() {
    const aboutImage = useAboutImage();
    const PORTFOLIO_LIVE = usePortfolio();
    return (
        <div>
            <Seo
                title="About — Elizaveta von Stuben Imagery"
                description="About Elizaveta von Stuben Imagery — a refined photography and visual storytelling studio serving Scottsdale, Phoenix & New York."
                canonical="https://vonstuben.com/about"
            />
            {/* HERO — brand-tone video, copy preserved */}
            <HeroVideo
                dataTestId="about-hero"
                ariaLabel="About the studio — Elizaveta von Stuben Imagery."
            >
                <span
                    className="block font-sans uppercase text-[11px] sm:text-[12px]"
                    style={{ color: "#e6e1db", letterSpacing: "0.22em", ...heroTextShadow.light }}
                >
                    About &middot; The Studio
                </span>
                <h1
                    className="font-serif-display mt-3 sm:mt-4 text-4xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-[22ch]"
                    style={{ color: "#fbf8f4", ...heroTextShadow.heavy }}
                >
                    Elizaveta von Stubendorf.
                </h1>
                <p
                    className="mt-5 max-w-[64ch] text-sm sm:text-base leading-[1.75]"
                    style={{ color: "#f5f1eb", ...heroTextShadow.medium }}
                >
                    A refined photography and visual storytelling studio serving Scottsdale, Phoenix &amp; New York. Polished portraits, family stories, brand imagery, business content, fine&#8209;art prints, and mentoring.
                </p>
            </HeroVideo>

            <Section spacing="normal">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    <div className="lg:col-span-5">
                        <EditorialImage src={aboutImage.desktop} alt={aboutImage.alt || "Elizaveta von Stubendorf"} ratio={4 / 5} />
                    </div>
                    <div className="lg:col-span-7">
                        <Kicker>About · The Studio</Kicker>
                        <SerifTitle className="mt-3">Elizaveta von Stubendorf.</SerifTitle>
                            <p className="mt-6 text-base leading-[1.95] text-foreground/90">
                                Elizaveta von Stuben Imagery is a refined photography and visual storytelling studio serving Scottsdale and New York. The studio creates polished portraits, family stories, proposals, private events, brand imagery, business content, short films, fine&#8209;art prints, and mentoring.
                            </p>
                        <p className="mt-5 text-base leading-[1.95] text-foreground/80">
                            Trained in editorial photography and creative direction, Elizaveta approaches every engagement like a curator — considering palette, gesture, light, and pacing before a single frame is taken. The result is imagery that does not perform; it remembers.
                        </p>
                        <Separator className="my-8 bg-[hsl(var(--lux-brass))]/40 w-10" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm leading-[1.8]">
                            <div>
                                <div className="kicker mb-2">Philosophy</div>
                                <p>Restraint is the first creative decision. The best photograph in a session is rarely the loudest — it is the one that survives the silence afterward.</p>
                            </div>
                            <div>
                                <div className="kicker mb-2">Approach</div>
                                <p>Every shoot is guided. Posing, styling, location, and pacing are held for you — so all you bring is your presence.</p>
                            </div>
                            <div>
                                <div className="kicker mb-2">Direction</div>
                                <p>Brand sessions are treated as campaigns. Personal sessions are treated as private archives. Both demand the same level of care.</p>
                            </div>
                            <div>
                                <div className="kicker mb-2">Studio</div>
                                <p>An editorial eye shaped by New York, serving clients in Scottsdale, Phoenix, New York, and select destinations.</p>
                            </div>
                        </div>
                        <Link to="/inquire" className="btn-request mt-10 inline-flex">
                            Request Availability
                        </Link>
                    </div>
                </div>
            </Section>

            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border">
                <Kicker>Selected work</Kicker>
                <SerifTitle className="mt-3">From the archive.</SerifTitle>
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                    {PORTFOLIO_LIVE.slice(0, 8).map((p, i) => (
                        <EditorialImage key={p.id} src={p.image} alt={p.title} ratio={i % 2 === 0 ? 4 / 5 : 1} />
                    ))}
                </div>
            </Section>
        </div>
    );
}
